import {
  listPagePublications,
  restorePagePublication,
  type PuckPageRecord,
  type PuckPublicationItem,
} from "@/api/cms-puck/api";
import { preserveCanvasScroll } from "@/lib/cms-puck/preserve-canvas-scroll";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { History, RotateCcw } from "lucide-react";
import type { SupportedLocale } from "@/i18n";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

const pubsKey = (pageId: string) => ["cms-puck", "publications", pageId] as const;

export function PublicationHistoryDialog({
  pageId,
  lang,
  open,
  onOpenChange,
  onRestored,
}: {
  pageId: string;
  lang: SupportedLocale;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestored?: (record: PuckPageRecord) => void;
}) {
  const cmsNew = useCmsNew();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const pubsQuery = useQuery({
    queryKey: pubsKey(pageId),
    queryFn: () => listPagePublications(pageId),
    enabled: open && !!pageId,
  });

  const restoreMutation = useMutation({
    mutationFn: (pubId: string) =>
      restorePagePublication(pageId, pubId, lang),
    onSuccess: (record) => {
      toast({
        title: cmsNew("cms.new.auto.title.brouillon_restaure", "Brouillon restauré"),
        description: cmsNew("cms.new.auto.description.la_version_selectionnee_a_ete_chargee_dans_lediteur", "La version sélectionnée a été chargée dans l'éditeur."),
      });
      void queryClient.invalidateQueries({ queryKey: pubsKey(pageId) });
      onRestored?.(record);
      onOpenChange(false);
    },
    onError: () => {
      toast({
        title: cmsNew("cms.new.auto.title.restauration_impossible", "Restauration impossible"),
        variant: "error",
      });
    },
  });

  const items = pubsQuery.data ?? [];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) preserveCanvasScroll();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History size={18} />
            {cmsNew("cms.new.publication.history.title", "Historique des publications")}
          </DialogTitle>
          <DialogDescription>
            {cmsNew(
              "cms.new.publication.history.description",
              "Chaque publication enregistre un snapshot (layout + traductions). La restauration remplace le brouillon courant sans republier.",
            )}
          </DialogDescription>
        </DialogHeader>

        {pubsQuery.isLoading ? (
          <p className="text-sm text-slate-400">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500">
            {cmsNew(
              "cms.new.publication.history.empty",
              "Aucune version enregistrée — publie la page au moins une fois.",
            )}
          </p>
        ) : (
          <ul className="max-h-64 space-y-2 overflow-y-auto">
            {items.map((item: PuckPublicationItem) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-2 rounded border border-slate-200 px-3 py-2"
              >
                <span className="text-sm text-slate-700">
                  {formatDate(item.createdAt)}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={restoreMutation.isPending}
                  onClick={() => restoreMutation.mutate(item.id)}
                >
                  <RotateCcw size={14} className="mr-1" />
                  {cmsNew("cms.new.publication.history.restore", "Restaurer")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}
